import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, History, Layers, Pencil, Play, RefreshCw } from 'lucide-react';
import { formBuilderApi } from './api';
import { AuditLogItem, FormSummary } from './types';
import { secondaryButton, Skeleton } from './ui';
import { Change, ChangeList } from './components/ChangeList';
import { ROUTES } from '../../config/links';

const PAGE_SIZE = 50;
const ACTIONS: AuditLogItem['action'][] = ['created', 'duplicated', 'updated', 'published', 'rolled_back', 'deleted', 'restored'];
const ACTION_STYLE: Record<AuditLogItem['action'], string> = {
  created: 'bg-emerald-100 text-emerald-700',
  duplicated: 'bg-sky-100 text-sky-700',
  updated: 'bg-amber-100 text-amber-700',
  published: 'bg-violet-100 text-violet-700',
  rolled_back: 'bg-orange-100 text-orange-700',
  deleted: 'bg-red-100 text-red-700',
  restored: 'bg-teal-100 text-teal-700',
};

const summarize = (log: AuditLogItem): string => {
  const d = log.details || {};
  const parts: string[] = [];
  if (log.action === 'updated') {
    parts.push(d.schema_changed ? `${(d.changes || []).length || ''} schema change${(d.changes || []).length === 1 ? '' : 's'}`.trim() : 'No schema changes');
    if (d.name) parts.push(`Renamed "${d.name.from}" → "${d.name.to}"`);
    if (d.description_changed) parts.push('Description changed');
  }
  if (log.action === 'duplicated' && d.source_form_id) parts.push(`Copied from v${d.source_version ?? '?'} of form ${d.source_form_id}`);
  if (log.action === 'created') parts.push('Draft v1 created');
  if (log.action === 'published') { parts.push(`Version ${log.version ?? '?'} published`); if (d.note) parts.push(`“${d.note}”`); }
  if (log.action === 'rolled_back') { parts.push(`Rolled back to v${d.restored_from ?? '?'} (new v${log.version ?? '?'}); previous live was v${d.previous_live ?? '?'}`); if (d.note) parts.push(`Reason: “${d.note}”`); }
  if (log.action === 'restored') parts.push('Form restored');
  if (log.action === 'deleted') parts.push('Form deleted');
  return parts.join(' · ');
};

export default function FormAuditLogPage() {
  const { formId = '' } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [form, setForm] = useState<FormSummary>();
  const [filter, setFilter] = useState<AuditLogItem['action'] | 'all'>('all');

  const load = (reset: boolean) => {
    setLoading(true);
    formBuilderApi
      .auditLogs(formId, { limit: PAGE_SIZE, offset: reset ? 0 : logs.length })
      .then((rows) => {
        setLogs((current) => (reset ? rows : [...current, ...rows]));
        setHasMore(rows.length === PAGE_SIZE);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || e?.message || 'Failed to load audit logs'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(true); }, [formId]);
  // Inactive forms 404 here; then the form-specific shortcuts are simply hidden.
  useEffect(() => { setForm(undefined); formBuilderApi.get(formId).then(setForm).catch(() => setForm(undefined)); }, [formId]);

  const formName = logs.find((l) => l.form_name)?.form_name;
  const visible = filter === 'all' ? logs : logs.filter((l) => l.action === filter);

  return (
    <div className="min-h-full bg-slate-50 p-3 md:p-8">
      <header className="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600"><History className="h-3.5 w-3.5" /> Audit trail</div>
          {loading && logs.length === 0 ? <Skeleton className="mt-1 h-7 w-56" /> : <h1 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{form?.name || formName || 'Form'}</h1>}
          {form?.description && <p className="mt-1 max-w-2xl text-xs text-slate-600">{form.description}</p>}
          <p className="mt-1 text-xs text-slate-500">Every create, update, publish, rollback, duplicate, delete and restore of this form, newest first.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className={secondaryButton} onClick={() => load(true)} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          {form && <button className={secondaryButton} onClick={() => navigate(ROUTES.formBuilderEdit(formId))}><Pencil className="h-3.5 w-3.5" /> Edit form</button>}
          {form && <button className={secondaryButton} onClick={() => navigate(ROUTES.formBuilderVersions(formId))}><Layers className="h-3.5 w-3.5" /> Versions</button>}
          {form?.status === 'published' && <button className={secondaryButton} onClick={() => navigate(ROUTES.formRuntime(formId))} title="Open published runtime"><Play className="h-3.5 w-3.5" /> Runtime</button>}
          <button className={secondaryButton} onClick={() => navigate(ROUTES.FORM_BUILDER)}><ArrowLeft className="h-3.5 w-3.5" /> All forms</button>
        </div>
      </header>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {(['all', ...ACTIONS] as const).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setFilter(a)}
            className={`rounded-full px-3 py-1 text-[11px] font-medium capitalize transition ${filter === a ? 'bg-slate-800 text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
          >
            {a}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[720px] text-left text-[12px]">
          <thead className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5">When</th>
              <th className="px-4 py-2.5">Action</th>
              <th className="px-4 py-2.5">Version</th>
              <th className="px-4 py-2.5">User</th>
              <th className="px-4 py-2.5">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && logs.length === 0 && (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} aria-hidden="true">
                    <td className="px-4 py-3"><Skeleton className="h-4 w-32" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-20 rounded-full" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-8" /></td>
                    <td className="px-4 py-3"><div className="space-y-1.5"><Skeleton className="h-4 w-28" /><Skeleton className="h-2.5 w-36" /></div></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-64 max-w-full" /></td>
                  </tr>
                ))}
              </>
            )}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No audit entries{filter !== 'all' ? ` for “${filter}”` : ''}.</td></tr>
            )}
            {visible.map((log) => (
              <tr key={log.id} className="align-top hover:bg-slate-50/60">
                <td className="whitespace-nowrap px-4 py-3 text-slate-600">{new Date(log.created_at).toLocaleString()}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${ACTION_STYLE[log.action] || 'bg-slate-100 text-slate-600'}`}>{log.action}</span></td>
                <td className="px-4 py-3 text-slate-600">{log.version != null ? `v${log.version}` : '—'}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-700">{log.actor_name || 'Unknown'}</div>
                  {log.actor_id && <div className="font-mono text-[10px] text-slate-400">{log.actor_id}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <div>{summarize(log) || '—'}</div>
                  {Array.isArray(log.details?.changes) && log.details!.changes.length > 0 && <ChangeList changes={log.details!.changes as Change[]} />}
                  {log.details && Object.keys(log.details).length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[10px] text-primary-600">Raw details</summary>
                      <pre className="mt-1 overflow-x-auto rounded-md bg-slate-50 p-2 text-[10px] text-slate-600">{JSON.stringify(log.details, null, 2)}</pre>
                    </details>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="mt-3 flex justify-center">
          <button className={secondaryButton} onClick={() => load(false)} disabled={loading}>{loading ? 'Loading…' : 'Load more'}</button>
        </div>
      )}
    </div>
  );
}
